import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check, Phone, Sparkles } from "lucide-react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import ListenButton from "../components/ListenButton";
import { allPrograms, company } from "../mock";

export default function LoanProgramDetail() {
  const { slug } = useParams();
  const program = allPrograms.find((p) => p.slug === slug);

  if (!program) {
    return (
      <main>
        <PageHero title="Program Not Found" crumb="Loan Programs" />
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <p className="text-slate-600">Sorry, we couldn't find that loan program.</p>
          <Link to="/loan-programs" className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#0b1f3a] px-6 py-3 font-bold text-white">
            View All Programs
          </Link>
        </div>
      </main>
    );
  }

  const others = allPrograms.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <main>
      <PageHero title={program.title} crumb={program.title} subtitle={program.short} />

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-3">
          {/* Main */}
          <div className="lg:col-span-2">
            <Reveal>
              <img src={program.image} alt={program.title} className="rounded-2xl w-full aspect-[16/9] object-cover shadow-lg" />
            </Reveal>
            <Reveal delay={80}>
              <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
                <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-[#0b1f3a]">
                  About {program.title}
                </h2>
                <ListenButton
                  testId="program-listen-btn"
                  label="Listen to summary"
                  text={`${program.title}. ${program.short}. ${program.body} Program highlights include: ${program.highlights.join(". ")}.`}
                />
              </div>
              <p className="mt-5 text-slate-600 leading-relaxed text-lg">{program.body}</p>

              <h3 className="font-display font-bold text-xl text-[#0b1f3a] mt-10">Program Highlights</h3>
              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                {program.highlights.map((h) => (
                  <div key={h} className="flex items-start gap-3 rounded-xl bg-slate-50 p-4 ring-1 ring-slate-100">
                    <div className="h-6 w-6 rounded-full bg-[#c6a15b]/15 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="h-4 w-4 text-[#b08e46]" />
                    </div>
                    <span className="text-slate-700">{h}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Sidebar */}
          <div>
            <Reveal delay={120}>
              <div className="rounded-2xl ring-1 ring-slate-100 shadow-sm overflow-hidden sticky top-28">
                <div className="bg-[#0b1f3a] px-6 py-5">
                  <h3 className="font-display font-bold text-white text-lg">Program Snapshot</h3>
                </div>
                <div className="p-6 space-y-4">
                  {program.facts.map((f) => (
                    <div key={f.k} className="flex items-center justify-between border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                      <span className="text-sm text-slate-500">{f.k}</span>
                      <span className="text-sm font-semibold text-[#0b1f3a]">{f.v}</span>
                    </div>
                  ))}
                  <Link to="/apply" className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-md bg-[#c6a15b] px-5 py-3 font-bold text-[#0b1f3a] hover:bg-[#d8bc85] transition-colors">
                    Apply Now <ArrowRight className="h-4 w-4" />
                  </Link>
                  <button
                    onClick={() =>
                      window.dispatchEvent(
                        new CustomEvent("ltr-open-chat", {
                          detail: { message: `I'm interested in ${program.title}. Do I qualify, and what are the requirements?` },
                        })
                      )
                    }
                    data-testid="see-if-i-qualify-btn"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-[#0b1f3a] px-5 py-3 font-bold text-white hover:bg-[#13294b] transition-colors"
                  >
                    <Sparkles className="h-4 w-4 text-[#c6a15b]" /> See If I Qualify
                  </button>
                  <a href={`tel:${company.phoneTel}`} className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 px-5 py-3 font-semibold text-[#0b1f3a] hover:bg-slate-50 transition">
                    <Phone className="h-4 w-4" /> {company.phoneDisplay}
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Other programs */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-[#0b1f3a]">Explore Other Programs</h2>
          <div className="mt-8 grid gap-7 md:grid-cols-3">
            {others.map((p) => (
              <Link key={p.slug} to={`/loan-programs/${p.slug}`} className="group block rounded-2xl bg-white overflow-hidden ring-1 ring-slate-100 hover:shadow-xl transition-all">
                <div className="relative h-40 overflow-hidden">
                  <img src={p.image} alt={p.title} className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0b1f3a]/70 to-transparent" />
                </div>
                <div className="p-5">
                  <h3 className="font-display font-bold text-[#0b1f3a] group-hover:text-[#b08e46] transition-colors">{p.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
